import { ReportExportButtons } from '../components/ReportExportButtons'
import { useEffect, useState, useCallback } from 'react'
import { funnelsApi, type LeadConversionFunnel, type PracticeRates } from '../api/funnels'
import {
  callCenterReportApi,
  type CallCenterOperatorsReport,
  type SpeedToLeadReport,
} from '../api/callCenterReport'
import { dataSourceBadge, type WithDataSource } from '../api/reportingAggregates'
import {
  Button, Callout, Card, CardContent, CardHeader, DataTable, FilterBar, Icon, LinearProgress, LoadingState, Page,
  PageHeader, StatCard, StatGrid, StatusPill, TextField, ChartCard, FunnelChart,
} from '../components/ui'
import { LeadChannelChartCard } from '../components/StatsCharts'
import { chartRange, type ChartRange } from '../components/chartRange'

/**
 * TASK-0029 / M14 slice 1: real Lead Conversion Funnel view replacing the `/funnels`
 * PlaceholderPage — spec section 95 ("Funnel Lead") + section 96 ("Conversion Rate"). This SPA
 * does no client-side route gating (same pattern as ImplementationStatusPage.tsx/ApprovalsPage.tsx
 * — the real gate is the backend's [Authorize(PolicyNames.Authenticated)] on
 * GET /api/funnels/lead-conversion); a caller who somehow reaches this page without a valid
 * session just gets a plain error message from `load`, same as those pages' `catch`.
 */

const formatNum = (v: number): string => v.toLocaleString('it-IT')

/** Rates from the backend are 0..1 fractions rounded to 4 decimals, or null for an empty
 * denominator (not a real 0%) — render null as an em-dash, never as "0%". */
function formatPct(v: number | null): string {
  if (v === null) return '—'
  return (v * 100).toLocaleString('it-IT', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + '%'
}

const formatMin = (v: number | null): string =>
  v === null ? '—' : v.toLocaleString('it-IT', { minimumFractionDigits: 0, maximumFractionDigits: 1 }) + ' min'

/**
 * F-052 — report call center: per operatore e speed to lead per fonte. Periodo proprio (vuoto = ultimi 30
 * giorni, deciso dal backend). Un EqualOperatore riceve solo la propria riga; gli altri ruoli non
 * autorizzati ricevono 403 e qui compare solo il messaggio. Nessun importo.
 */
function CallCenterReportSection() {
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [ops, setOps] = useState<CallCenterOperatorsReport | null>(null)
  const [stl, setStl] = useState<SpeedToLeadReport | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const load = useCallback(async (f: string, t: string) => {
    setLoading(true)
    setError('')
    try {
      const filters = { from: f || undefined, to: t || undefined }
      const [o, s] = await Promise.all([callCenterReportApi.operators(filters), callCenterReportApi.speedToLead(filters)])
      setOps(o)
      setStl(s)
    } catch (e) {
      setOps(null)
      setStl(null)
      setError(e instanceof Error ? e.message : 'Errore')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load('', '')
  }, [load])

  return (
    <Card variant="outlined">
      <CardHeader
        title="Call center: operatori e speed to lead"
        actions={
          <span className="d-flex align-items-center gap-3">
            {ops && ops.scope === 'all' && <a href="/call-center" className="md-typescale-body-small">Cruscotto del responsabile</a>}
            {ops && <span className="text-muted md-typescale-body-small">{ops.scope === 'own' ? 'Solo i tuoi dati' : 'Tutti gli operatori'}</span>}
            {ops && (() => {
              const badge = dataSourceBadge((ops as CallCenterOperatorsReport & WithDataSource).dataSource,
                (ops as CallCenterOperatorsReport & WithDataSource).computedAt)
              return badge && (
                <StatusPill
                  tone={badge.aggregate ? 'info' : 'neutral'}
                  title={badge.title}
                  data-testid="data-source-badge"
                >
                  Fonte dati: {badge.text}
                </StatusPill>
              )
            })()}
          </span>
        }
      />
      <CardContent>
        <FilterBar
          className="mb-3"
          actions={
            <>
              <Button variant="filled" size="sm" onClick={() => void load(from, to)}>Filtra</Button>
              <span className="text-muted md-typescale-body-small">Senza date: ultimi 30 giorni. Massimo 366 giorni.</span>
            </>
          }
        >
          <TextField type="date" label="Da" value={from} onChange={e => setFrom(e.target.value)} />
          <TextField type="date" label="A" value={to} onChange={e => setTo(e.target.value)} />
        </FilterBar>

        {loading ? (
          <LoadingState />
        ) : error ? (
          <Callout tone="warning">Report call center non disponibile. {error}</Callout>
        ) : (
          <>
            <h3 className="md-typescale-title-medium">Per operatore</h3>
            {ops && ops.operators.length === 0 ? (
              <div className="text-muted md-typescale-body-small mb-3">
                Nessuna chiamata registrata nel periodo. Le righe compaiono quando gli operatori registrano i
                tentativi dalla scheda lead.
              </div>
            ) : (
              <div className="mb-2">
                <DataTable variant="plain">
                  <thead>
                    <tr>
                      <th>Operatore</th>
                      <th className="text-end">Chiamate</th>
                      <th className="text-end">Contatti utili</th>
                      <th className="text-end">Lead lavorati</th>
                      <th className="text-end">Tempo medio 1° tentativo</th>
                      <th className="text-end">Appuntamenti fissati</th>
                      <th className="text-end">Effettuati</th>
                      <th className="text-end">No-show</th>
                      <th className="text-end">Contratti</th>
                      <th className="text-end">Attivazioni</th>
                      <th className="text-end">Conversione</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ops?.operators.map(r => (
                      <tr key={r.operatorUserId}>
                        <td>Utente #{r.operatorUserId}</td>
                        <td className="text-end md-num">{formatNum(r.calls)}</td>
                        <td className="text-end md-num">{formatNum(r.usefulContacts)}</td>
                        <td className="text-end md-num">{formatNum(r.distinctLeadsWorked)}</td>
                        <td className="text-end md-num">{formatMin(r.averageMinutesToFirstAttempt)}</td>
                        <td className="text-end md-num">{formatNum(r.appointmentsSet)}</td>
                        <td className="text-end md-num">{formatNum(r.appointmentsCompleted)}</td>
                        <td className="text-end md-num">{formatNum(r.appointmentsNoShow)}</td>
                        <td className="text-end md-num">{formatNum(r.contractsGenerated)}</td>
                        <td className="text-end md-num">{formatNum(r.activations)}</td>
                        <td className="text-end md-num">{formatPct(r.conversionRate)}</td>
                      </tr>
                    ))}
                  </tbody>
                </DataTable>
              </div>
            )}
            {ops && (
              <div className="text-muted md-typescale-body-small mb-3">
                Conversione = {ops.conversionRateFormula}. Non disponibili: {ops.gaps.map(g => g.split(':')[0]).join(', ')}.
              </div>
            )}

            <h3 className="md-typescale-title-medium">Speed to lead per fonte</h3>
            {stl && stl.total.total === 0 ? (
              <div className="text-muted md-typescale-body-small">Nessun lead creato nel periodo con una fonte riconosciuta.</div>
            ) : (
              stl && (
                <DataTable variant="plain">
                  <thead>
                    <tr>
                      <th>Fonte</th>
                      <th className="text-end">Obiettivo</th>
                      <th className="text-end">Lead</th>
                      <th className="text-end">Entro obiettivo</th>
                      <th className="text-end">Fuori obiettivo</th>
                      <th className="text-end">In attesa</th>
                      <th className="text-end">Media</th>
                      <th className="text-end">Mediana</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...stl.sources, stl.total].map(r => (
                      <tr key={r.source} className={r.source === 'total' ? 'md-typescale-emphasized-body-medium' : ''}>
                        <td>{r.source === 'total' ? 'Totale' : r.label}</td>
                        <td className="text-end md-num">{r.targetMinutes === null ? '—' : `${r.targetMinutes} min`}</td>
                        <td className="text-end md-num">{formatNum(r.total)}</td>
                        <td className="text-end md-num">{formatNum(r.withinSla)}</td>
                        <td className="text-end md-num">{formatNum(r.outsideSla)}</td>
                        <td className="text-end md-num">{formatNum(r.pending)}</td>
                        <td className="text-end md-num">{formatMin(r.averageMinutes)}</td>
                        <td className="text-end md-num">{formatMin(r.medianMinutes)}</td>
                      </tr>
                    ))}
                  </tbody>
                </DataTable>
              )
            )}
            {stl && (
              <div className="text-muted md-typescale-body-small">
                {stl.targetsProvisional && 'Gli obiettivi di tempo sono provvisori, in attesa di conferma della direzione. '}
                Un lead senza tentativi oltre l&apos;obiettivo conta come fuori obiettivo; entro l&apos;obiettivo è in attesa.
                {stl.leadsWithUnrecognisedSource > 0 &&
                  ` ${formatNum(stl.leadsWithUnrecognisedSource)} lead con fonte non riconosciuta non sono conteggiati.`}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}

interface Stage {
  key: string
  label: string
  count: number
  /** Conversion rate FROM the previous stage shown, INTO this stage — null on the first stage
   * (no previous stage) or when the denominator was 0. Each label spells out its exact
   * numerator/denominator since they are not always simply "previous stage -> this stage" (e.g.
   * Appointment Rate divides by contacted leads, not by qualified leads). */
  rate: { value: number | null; label: string } | null
}

function FunnelBar({ stage, maxCount }: { stage: Stage; maxCount: number }) {
  const widthPct = maxCount === 0 ? 0 : Math.max(2, (stage.count / maxCount) * 100)
  return (
    <div className="mb-2">
      {stage.rate && (
        <div className="d-flex align-items-center gap-2 mb-1 ps-1">
          <Icon name="arrow_downward" size={18} className="text-muted" />
          <span className="text-muted md-typescale-body-small">
            {stage.rate.label}: <strong>{formatPct(stage.rate.value)}</strong>
          </span>
        </div>
      )}
      <div className="d-flex align-items-center gap-3">
        <LinearProgress className="flex-grow-1" value={widthPct} aria-label={stage.label} />
        <div className="md-u-minw-220">
          <div className="md-typescale-emphasized-body-medium">{stage.label}</div>
          <div className="text-muted md-typescale-body-small">{formatNum(stage.count)}</div>
        </div>
      </div>
    </div>
  )
}

export function FunnelsPage() {
  const [data, setData] = useState<LeadConversionFunnel | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')

  // REQ-027-002: section-27 "Activation Rate" (activated / SUBMITTED contracts) — a separate
  // endpoint from `leadConversion` above (no `from`/`to` filter, its own visible-agent scope), so
  // it gets its own state and its own load/error handling rather than being folded into `data`.
  const [practiceRates, setPracticeRates] = useState<PracticeRates | null>(null)
  const [practiceRatesError, setPracticeRatesError] = useState('')

  // Intervallo applicato, per il grafico dei canali (filtri vuoti = ultimi 12 mesi).
  const [range, setRange] = useState<ChartRange>(() => chartRange())

  const load = useCallback(async (f: string, t: string) => {
    setLoading(true)
    setError('')
    setRange(chartRange(f || undefined, t || undefined))
    try {
      const filters: { from?: string; to?: string } = {}
      if (f) filters.from = f
      if (t) filters.to = t
      const result = await funnelsApi.leadConversion(filters)
      setData(result)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore')
    } finally {
      setLoading(false)
    }
  }, [])

  const loadPracticeRates = useCallback(async () => {
    setPracticeRatesError('')
    try {
      setPracticeRates(await funnelsApi.practiceRates())
    } catch (e) {
      setPracticeRatesError(e instanceof Error ? e.message : 'Errore')
    }
  }, [])

  useEffect(() => {
    void load(from, to)
    void loadPracticeRates()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function applyFilter() {
    void load(from, to)
  }

  function resetFilter() {
    setFrom('')
    setTo('')
    void load('', '')
  }

  const stages: Stage[] = data
    ? [
        { key: 'totalLeads', label: 'Lead totali', count: data.stages.totalLeads, rate: null },
        {
          key: 'contacted',
          label: 'Contattati',
          count: data.stages.contacted,
          rate: { value: data.conversionRates.contactRate, label: 'Contact Rate (contattati / lead totali)' },
        },
        {
          key: 'qualified',
          label: 'Qualificati',
          count: data.stages.qualified,
          rate: { value: data.conversionRates.qualificationRate, label: 'Qualification Rate (qualificati / contattati)' },
        },
        {
          key: 'appointmentsSet',
          label: 'Appuntamenti fissati',
          count: data.stages.appointmentsSet,
          rate: { value: data.conversionRates.appointmentRate, label: 'Appointment Rate (appuntamenti fissati / contattati)' },
        },
        {
          key: 'appointmentsCompleted',
          label: 'Appuntamenti effettuati',
          count: data.stages.appointmentsCompleted,
          rate: { value: data.conversionRates.showRate, label: 'Show Rate (effettuati / fissati)' },
        },
        {
          key: 'contracts',
          label: 'Contratti',
          count: data.stages.contracts,
          rate: { value: data.conversionRates.closingRate, label: 'Closing Rate (contratti / appuntamenti effettuati)' },
        },
        {
          key: 'activations',
          label: 'Attivazioni',
          count: data.stages.activations,
          rate: { value: data.conversionRates.activationRateOnContracts, label: 'Activation Rate su contratti (attivazioni / contratti)' },
        },
      ]
    : []

  const maxCount = data ? data.stages.totalLeads : 0

  return (
    <Page>
      <PageHeader
        title="Funnel"
        icon="filter_alt"
        subtitle="Funnel di conversione lead"
        actions={<ReportExportButtons title="Funnel di conversione lead" />}
      />

      <FilterBar
        actions={
          <>
            <Button variant="filled" size="sm" onClick={applyFilter}>Filtra</Button>
            <Button variant="outlined" size="sm" onClick={resetFilter}>Azzera</Button>
          </>
        }
      >
        <TextField type="date" label="Da (data creazione lead)" value={from} onChange={e => setFrom(e.target.value)} />
        <TextField type="date" label="A" value={to} onChange={e => setTo(e.target.value)} />
      </FilterBar>

      {loading ? (
        <Card variant="outlined"><LoadingState /></Card>
      ) : error ? (
        <Callout tone="error">Non è stato possibile caricare il funnel. {error}</Callout>
      ) : data ? (
        <>
        <ChartCard title="Imbuto di conversione" subtitle="Dai lead totali alle attivazioni">
          <FunnelChart
            label="Imbuto di conversione dei lead"
            steps={stages.map(s => ({ id: s.key, label: s.label, value: s.count }))}
            emptyTitle="Nessun lead nel periodo"
          />
        </ChartCard>
        <Card variant="outlined">
          <CardContent>
            {stages.map(s => (
              <FunnelBar key={s.key} stage={s} maxCount={maxCount} />
            ))}
          </CardContent>
        </Card>
        </>
      ) : null}

      <LeadChannelChartCard range={range} />

      {data && (
        <section aria-labelledby="funnel-extra-kpi">
          <h2 id="funnel-extra-kpi" className="md-typescale-title-large mb-2">Indicatori aggiuntivi</h2>
          <StatGrid>
            <StatCard label="Lead to Sale" supporting="contratti su lead" value={formatPct(data.conversionRates.leadToSale)} />
            <StatCard label="Lead to Activation" supporting="attivazioni su lead" value={formatPct(data.conversionRates.leadToActivation)} />
            <StatCard
              label="Activation Rate (attivati / pratiche inviate)"
              value={
                practiceRatesError ? (
                  <span className="text-danger md-typescale-body-small">{practiceRatesError}</span>
                ) : (
                  <>
                    {practiceRates ? formatPct(practiceRates.activationRateVsSubmitted) : '—'}
                    {practiceRates && (
                      <div className="text-muted md-typescale-body-small">
                        {formatNum(practiceRates.activatedContractsCount)} /{' '}
                        {formatNum(practiceRates.submittedContractsCount)}
                      </div>
                    )}
                  </>
                )
              }
            />
            {/* REQ-027-003: same practiceRates load/error state as Activation Rate above — one
                endpoint, one loading/error lifecycle, both KPIs rendered from it. */}
            <StatCard
              label="Reject Rate (rifiutati / pratiche inviate)"
              value={
                practiceRatesError ? (
                  <span className="text-danger md-typescale-body-small">{practiceRatesError}</span>
                ) : (
                  <>
                    {practiceRates ? formatPct(practiceRates.rejectRate) : '—'}
                    {practiceRates && (
                      <div className="text-muted md-typescale-body-small">
                        {formatNum(practiceRates.rejectedContractsCount)} /{' '}
                        {formatNum(practiceRates.submittedContractsCount)}
                      </div>
                    )}
                  </>
                )
              }
            />
            {/* REQ-027-004: same practiceRates load/error state as the two rates above, but this
                KPI's denominator is totalContractsCount (ALL visible practices), NOT
                submittedContractsCount — deliberately not reusing the "/ pratiche inviate" caption. */}
            <StatCard
              label="Integration Rate (integrazioni / pratiche totali)"
              value={
                practiceRatesError ? (
                  <span className="text-danger md-typescale-body-small">{practiceRatesError}</span>
                ) : (
                  <>
                    {practiceRates ? formatPct(practiceRates.integrationRate) : '—'}
                    {practiceRates && (
                      <div className="text-muted md-typescale-body-small">
                        {formatNum(practiceRates.integrationRequiredContractsCount)} /{' '}
                        {formatNum(practiceRates.totalContractsCount)}
                      </div>
                    )}
                  </>
                )
              }
            />
          </StatGrid>
        </section>
      )}

      <CallCenterReportSection />

      <Callout tone="info">
        Lo stadio &quot;Opportunità&quot; previsto dalla specifica (sezione 95) non è incluso in
        questo funnel: <code>Opportunity</code> non esiste ancora come entità reale in questo
        sistema (solo <code>Pipeline</code>/<code>PipelineStage</code> — vedi{' '}
        <code>GLOSSARY.md</code>). Non è stato sostituito con un proxy Pipeline/Stage etichettato
        in modo fuorviante.
      </Callout>
    </Page>
  )
}
